import { useMemo } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { FieldCategoryGrid } from '@/components/layout/field-row'
import { TabActions, TabCard } from '@/components/layout/tab-card'
import { DirtyFieldChange } from '@/hooks'
import { Building } from '@/reducers/types'

import {
  PLACE_FEATURE_GROUP_TAB,
  PlaceFeatureGroup,
  PlaceFeatureGroupSchema,
  PlaceFeatureTab,
} from '../../../features/places-features.constants'
import { getFeaturesByGroup } from '../../../features/places-features.rules'
import { PlaceFeatureGroupSection } from '../../flags/place-feature-group'
import {
  PlaceFeatureGroupedList,
  PlaceFeaturesToggleAll,
} from '../../flags/place-feature-grouped-list'
import { usePlaceFlagsForm } from '../../flags/use-place-flags-form'
import { usePlaceOpenGroups } from '../../flags/use-place-open-groups'
import { PlaceFeaturesView } from '../../place-details.types'

interface FeaturesTabProps {
  building: Building
  isAdmin: boolean
  onDirtyChange: (isDirty: boolean, getChanges?: () => DirtyFieldChange[]) => void
  view?: PlaceFeaturesView
}

export const FEATURE_GROUPS = PlaceFeatureGroupSchema.options.filter(
  (group) => PLACE_FEATURE_GROUP_TAB[group] === PlaceFeatureTab.features,
)

const getGroupSize = (group: PlaceFeatureGroup) =>
  getFeaturesByGroup(group).filter((feature) => !feature.hidden).length

// Note: longest cards first, so the top row holds the long groups and the bottom row the short ones
export const FEATURE_GROUPS_BY_SIZE = [...FEATURE_GROUPS].sort(
  (a, b) => getGroupSize(b) - getGroupSize(a),
)

export const FeaturesTab = ({
  building,
  isAdmin,
  onDirtyChange,
  view = PlaceFeaturesView.classic,
}: FeaturesTabProps) => {
  const { t } = useTranslation(['organisation'])
  const {
    errorFeatures,
    errorMessage,
    form,
    handleCancel,
    handleEdit,
    handleSave,
    isDirty,
    isEditing,
    isSaving,
  } = usePlaceFlagsForm({ building, isAdmin, onDirtyChange, tab: PlaceFeatureTab.features })

  const { areAllOpen, openGroups, toggleAll, toggleGroup } = usePlaceOpenGroups(FEATURE_GROUPS)

  const isGrouped = view === PlaceFeaturesView.grouped

  const customLabels = useMemo(
    () =>
      building.customAccessName
        ? {
            customAccessAvailable: t('organisation:places.building.customAccessToggle', {
              name: building.customAccessName,
            }),
          }
        : undefined,
    [building.customAccessName, t],
  )

  return (
    <FormProvider {...form}>
      <TabCard
        actions={
          <>
            {isGrouped && !isEditing && (
              <PlaceFeaturesToggleAll areAllOpen={areAllOpen} onClick={toggleAll} />
            )}
            <TabActions
              isDirty={isDirty}
              isEditing={isEditing}
              isSaving={isSaving}
              onCancel={handleCancel}
              onEdit={handleEdit}
              onSave={handleSave}
            />
          </>
        }
        title={t('organisation:places.features.tab.title')}
      >
        {isGrouped ? (
          <PlaceFeatureGroupedList
            building={building}
            customLabels={customLabels}
            errorFeatures={errorFeatures}
            errorMessage={errorMessage}
            groups={FEATURE_GROUPS}
            isAdmin={isAdmin}
            isEditing={isEditing}
            openGroups={openGroups}
            onToggleGroup={toggleGroup}
          />
        ) : (
          <FieldCategoryGrid layout="rows">
            {FEATURE_GROUPS_BY_SIZE.map((group) => (
              <PlaceFeatureGroupSection
                key={group}
                showCount
                building={building}
                customLabels={customLabels}
                errorFeatures={errorFeatures}
                errorMessage={errorMessage}
                group={group}
                isAdmin={isAdmin}
                isEditing={isEditing}
              />
            ))}
          </FieldCategoryGrid>
        )}
      </TabCard>
    </FormProvider>
  )
}
